import { useCallback, useEffect, useState } from 'react';
import { useAdminApiV1 } from '../../../clients/quipli-service/admin/admin-api-v1';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { Location } from '../../../clients/quipli-service/location/location';
import { debounce, differenceWith, isEqual } from 'lodash';
import { toast } from '../../../util/toasts';

interface UseAdminLocationsProps {
  adminId?: number;
  fetchAtMount?: boolean;
}

export const useAdminLocations = ({ adminId, fetchAtMount = true }: UseAdminLocationsProps) => {
  const { listLocations } = useAPI();
  const { listAdminLocations, updateAdminUserLocations } = useAdminApiV1();

  // State
  const [isLoading, setIsLoading] = useState<boolean>(fetchAtMount);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [allAvailableLocations, setAllAvailableLocations] = useState<Location[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<Location[]>([]);
  const [areAllLocationsSelected, setAreAllLocationsSelected] = useState<boolean>(false);

  // Save admin locations with debounce
  const saveLocationSettings = useCallback(
    debounce(async function (locations: Location[], _adminId: number) {
      try {
        setIsSubmitting(true);
        await updateAdminUserLocations(
          _adminId,
          locations.map((location) => location.id),
        );
        toast('Locations updated successfully');
      } catch (err) {
        console.error(err);
        toast('Something wrong updating locations permissions', 'error');
      } finally {
        setHasUnsavedChanges(false);
        setIsSubmitting(false);
      }
    }, 600),
    [],
  );

  // Toggle individual location
  const toggleLocation = useCallback((location: Location, isEnabled: boolean) => {
    setHasUnsavedChanges(true);
    setSelectedLocations((prevLocations) => {
      if (isEnabled) {
        return [...prevLocations, location];
      } else {
        return prevLocations.filter((item) => item.id !== location.id);
      }
    });
  }, []);

  // Toggle all locations
  const toggleAllLocations = useCallback(() => {
    setHasUnsavedChanges(true);
    setSelectedLocations((prevLocations) => {
      const areAllCurrentlySelected = allAvailableLocations.every((location) =>
        prevLocations.some((selectedLocation) => selectedLocation.id === location.id),
      );

      if (!areAllCurrentlySelected) {
        return [...allAvailableLocations];
      } else {
        return [];
      }
    });
  }, [allAvailableLocations]);

  // Check if all locations are selected
  const checkIfAllLocationsSelected = useCallback((availableLocations: Location[], selectedLocations: Location[]) => {
    const areAllSelected = differenceWith(availableLocations, selectedLocations, isEqual).length === 0;
    setAreAllLocationsSelected(areAllSelected);
  }, []);

  // Fetch admin locations
  const fetchLocationSettings = useCallback(async (_adminId: number) => {
    try {
      const [availableLocations, adminLocations] = await Promise.all([
        listLocations(true),
        listAdminLocations(_adminId),
      ]);

      setAllAvailableLocations(availableLocations);
      setSelectedLocations(adminLocations);
      checkIfAllLocationsSelected(availableLocations, adminLocations);
    } catch (e: any) {
      toast('Error fetching locations', 'error');
    }
  }, []);

  // Initialize the hook
  const initializeLocationSettings = useCallback(async (_adminId: number) => {
    setIsLoading(true);
    await fetchLocationSettings(_adminId);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (fetchAtMount && adminId) {
      initializeLocationSettings(adminId);
    }
  }, [fetchAtMount, adminId, initializeLocationSettings]);

  useEffect(() => {
    if (!isSubmitting && hasUnsavedChanges && adminId) {
      saveLocationSettings(selectedLocations, adminId);
      checkIfAllLocationsSelected(allAvailableLocations, selectedLocations);
    }
  }, [selectedLocations, isSubmitting, hasUnsavedChanges, adminId]);

  return {
    // State
    isLoading,
    isSubmitting,
    allAvailableLocations,
    selectedLocations,
    areAllLocationsSelected,

    // Business logic
    toggleLocation,
    toggleAllLocations,
    saveLocationSettings,
    fetchLocationSettings,
  };
};
